import { useEffect } from 'react';

/**
 * useFocusTrap — traps keyboard focus inside a container while `active` is true.
 *
 * On activation: stores the previously focused element (or `returnFocusRef.current`)
 * and moves focus to `initialFocusRef.current` if provided, otherwise the first
 * focusable descendant of `ref.current`.
 *
 * While active: intercepts Tab / Shift+Tab to cycle focus inside the container.
 *
 * On deactivation/unmount: restores focus to the stored element if it is still
 * connected to the DOM.
 *
 * @param {React.RefObject<HTMLElement>} ref — container element
 * @param {{ active: boolean, initialFocusRef?: React.RefObject<HTMLElement>, returnFocusRef?: React.RefObject<HTMLElement> }} options
 */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function useFocusTrap(ref, { active, initialFocusRef, returnFocusRef } = {}) {
  useEffect(() => {
    if (!active) return undefined;
    const container = ref && ref.current;
    if (!container) return undefined;

    const restoreTarget =
      (returnFocusRef && returnFocusRef.current) ||
      (typeof document !== 'undefined' ? document.activeElement : null);

    const focusInitial = () => {
      if (initialFocusRef && initialFocusRef.current) {
        initialFocusRef.current.focus();
        return;
      }
      const focusables = container.querySelectorAll(FOCUSABLE_SELECTOR);
      if (focusables.length > 0) {
        focusables[0].focus();
      } else {
        // Make container itself focusable as a fallback so focus is contained.
        container.setAttribute('tabindex', '-1');
        container.focus();
      }
    };

    // Defer to next tick so the element is actually mounted/painted.
    const timer = window.setTimeout(focusInitial, 0);

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      const focusables = Array.from(
        container.querySelectorAll(FOCUSABLE_SELECTOR)
      ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);

      if (focusables.length === 0) {
        e.preventDefault();
        container.focus();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const activeEl = document.activeElement;

      if (e.shiftKey) {
        if (activeEl === first || !container.contains(activeEl)) {
          e.preventDefault();
          last.focus();
        }
      } else if (activeEl === last || !container.contains(activeEl)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', handleKeyDown);
      if (
        restoreTarget &&
        typeof restoreTarget.focus === 'function' &&
        document.body.contains(restoreTarget)
      ) {
        restoreTarget.focus();
      }
    };
  }, [active, ref, initialFocusRef, returnFocusRef]);
}
